/** The **per-person** half of the Microsoft Teams channel (plan §5a FR5–FR7,
 *  NG-1134, epic NG-886) — one person's link to their own Teams chat, across every
 *  workspace they are a member of.
 *
 *  A separate module from `msteams.ts` because it is a different resource with a
 *  different owner: that one is the workspace's *consent* (`workspaces/<slug>/…`,
 *  admin-only, singular), this one is the caller's own *links*
 *  (`users/me/integrations/msteams/links/`, self-scoped, one row per workspace). The
 *  only thing they share is the redirect-code vocabulary, which lives in `msteams.ts`
 *  because both purposes come back through the **same** callback endpoint.
 *
 *  Derived wholly from the committed OpenAPI schema, for the reason `msteams.ts` is:
 *  this surface is brand new, so there is no legacy hand-transcribed shape to
 *  half-migrate away from, and the `API contract fresh` CI job regenerates
 *  `api.generated.ts` from the branch's own serializers and fails on a diff. The
 *  aliases below keep the generator's names rather than inventing tidier ones, so a
 *  reader can find the serializer that produced each shape — the generator's
 *  `TeamsLinkWorkspace` is the *workspace reference*, not the row, and a
 *  better-sounding local name for the row is exactly how that trap gets set.
 */
import type { components } from "./api.generated";

/** What the person's link row is doing, from the backend's `TeamsUserLink.State`.
 *
 *  `unmatched` — a Teams install we have seen but cannot attribute to a Cognota
 *  account. `matched` — bound to this person. `blocked` — Teams refuses delivery.
 *  `uninstalled` — they removed the app from Teams. `unlinked` — they opted out **in
 *  Cognota**, which is a stronger statement than `uninstalled` and is why it is its
 *  own value rather than a reuse of it (NG-1134 decision 3): an install re-matches an
 *  uninstalled row, and must not silently re-match an opt-out.
 */
export type TTeamsLinkState = components["schemas"]["TeamsSelfLinkStateEnum"];

/** The caller's link on one workspace's connection.
 *
 *  `match_method` (`"oauth"` / `"email"` / `""`) is a free-text column server-side and
 *  arrives typed as `string`; the UI shows it nowhere. It is on the row because "why
 *  am I linked" is the first question a support conversation asks — and because
 *  `oauth` outranks `email`, which is what decides a rebind.
 *
 *  `installed` is the fact worth knowing: whether the backend has a `conversation_id`
 *  to send to. A matched link with none is *connected, not installed* — the identity
 *  is proven and nothing can be delivered, which is the one state a person can fix
 *  themselves.
 */
export type TTeamsSelfLink = components["schemas"]["TeamsSelfLink"];

/** One workspace the caller belongs to that **has** a Teams connection, plus their
 *  link on it.
 *
 *  Workspaces with no connection are absent from the payload rather than listed with
 *  a null connection — the serializer's docstring says so, and it is the opposite of
 *  the admin connection endpoint's one-shape rule for a reason: that one answers "is
 *  Teams on *here*" about a named workspace, so "no" is a real answer; this one
 *  answers "where can I use Teams", and a workspace whose admin never enabled it is
 *  not somewhere the person can do anything.
 *
 *  That is also why `connection_state` is typed wider than it can arrive: the
 *  serializer reuses the connection vocabulary, so the generated enum carries
 *  `not_enabled` too, and a workspace in that state has no live row and is never
 *  listed. Deliberately **not** narrowed with an `Exclude<>` alias here — the alias
 *  would have to be applied by hand at each read to mean anything, and an alias
 *  nothing applies is a claim about the payload that the compiler never checks.
 */
export type TTeamsWorkspaceLink = components["schemas"]["TeamsWorkspaceLink"];

/** What `GET`, and `POST …/unlink/`, both answer with — one shape for the read and
 *  for the write's result, so the caller replaces its view rather than merging two.
 *
 *  An envelope rather than a bare list because a top-level JSON array is the one shape
 *  nothing can be added to later without breaking every client that reads it. It used
 *  to carry an `install_url` deep link beside the rows; NG-1186 removed it, because
 *  `/l/app/<manifest id>` resolves only for a sideloaded app and every customer
 *  installs from their own org catalog under a catalog id we cannot read. The block
 *  spells the install out as steps instead.
 */
export type TTeamsSelfLinks = components["schemas"]["TeamsSelfLinks"];

/** What a **202** answers with: the card was built and handed to Teams (NG-1641).
 *
 *  `period_key` is the day the card is *about* and deduplicates on. A second press is
 *  **another 202**, carrying the next free day's key — never a copy of the first card,
 *  and never a refusal. (`409 already_sent` is the race alone: the key was free when
 *  the endpoint read it and was taken by the time it wrote, by a simultaneous press or
 *  by the Monday tick.) The two totals are what the summary counted, and the only part
 *  of this the UI shows — saying "sent" without them leaves the reader unable to tell a
 *  useful card from an empty one.
 */
export type TTeamsDigestSent = components["schemas"]["TeamsSelfDigestSent"];

/** What a **200** answers with: nothing was due, so nothing was sent.
 *
 *  A success rather than a refusal, deliberately — the person did nothing wrong and
 *  there is nothing to fix — which is why it shares a union with the 202 instead of
 *  being an error the caller catches. Not exported: the union below is the shape
 *  callers hold, and a second name for its quiet half is a name nothing would use.
 */
type TTeamsDigestNothingDue = components["schemas"]["TeamsSelfDigestNothingDue"];

/** The two shapes a manual send can **succeed** with, as one union.
 *
 *  Both are 2xx and neither is an error, so the service resolves with either and the
 *  caller branches on `sent` (via `isDigestSent`) rather than on a status code it
 *  never sees. The generated `sent` is a plain `boolean` on both halves — the backend
 *  documents the literal but the schema cannot express it — so the narrowing lives in
 *  one predicate beside the store rather than being re-derived per call site.
 */
export type TTeamsDigestSendResult = TTeamsDigestSent | TTeamsDigestNothingDue;

/** The body behind a **400 / 404 / 409**: a sentence and a machine-readable `code`.
 *
 *  The UI reads the code, never the sentence: `error` is written for a developer
 *  reading a response, and each code has its own copy here in both locales because
 *  each names a different thing to go and do.
 */
export type TTeamsDigestRefused = components["schemas"]["TeamsSelfDigestRefused"];

/** Every `code` the send endpoint can refuse with, as a closed set.
 *
 *  **Derived from the contract, not retyped.** `TeamsSelfDigestRefusalCodeEnum` is a real
 *  OpenAPI enum (the backend's `views_digest.SEND_REFUSAL_CODES` feeds a `ChoiceField`,
 *  and the component name is pinned in `ENUM_NAME_OVERRIDES` so a future `code` field
 *  cannot rename it out from under this file). The hand-kept list this replaced had no
 *  way to notice a code the backend added or dropped.
 *
 *  A runtime list is still needed — `digestRefusalCode` matches the value off the wire
 *  against it before it becomes half of an i18n key, so an unrecognised one reaches the
 *  reader as the generic failure rather than as a raw
 *  `…send_summary_refused.whatever` — and the map below is how that list is kept honest
 *  in **both** directions. `Record<TTeamsDigestRefusalCode, true>` refuses a key the
 *  contract does not have *and* refuses to be missing one, so a backend change to the
 *  enum is a type error here rather than silently-unreachable copy or an untranslated
 *  toast. A `satisfies readonly TTeamsDigestRefusalCode[]` on a plain array would only
 *  catch the first of those two.
 */
const REFUSAL_CODE_COVERAGE: Record<TTeamsDigestRefusalCode, true> = {
  no_workspace: true,
  not_member: true,
  no_connection: true,
  paused: true,
  no_link: true,
  not_installed: true,
  deactivated: true,
  not_receiving: true,
  switch_off: true,
  already_sent: true,
  too_many_manual_sends: true,
};

/** The same set as a list, for the membership test in `digestRefusalCode`.
 *
 *  The cast is sound by construction: the object above is typed as a total record over
 *  the generated enum, so its keys are exactly that enum's members. */
export const TEAMS_DIGEST_REFUSAL_CODES = Object.keys(
  REFUSAL_CODE_COVERAGE,
) as readonly TTeamsDigestRefusalCode[];

/** One refusal `code`, as the contract publishes it. */
export type TTeamsDigestRefusalCode = TTeamsDigestRefused["code"];
